// #Purphyt - Gestion de l'affichage precommande dans le composant delivery-estimation
import { ThemeEvents } from '@theme/events';

class DeliveryEstimation extends HTMLElement {
  // #Purphyt - Date précommande du produit/variante courant (sans les articles du panier)
  #productPreorderDate = '';

  constructor() {
    super();

    // Éléments DOM
    this.deliveryTextElement = null;
    this.minDateElement = null;
    this.betweenTextElement = null;
    this.maxDateElement = null;
    this.handleVariantUpdated = this.handleVariantUpdated.bind(this);
    this.handleCartUpdated = this.handleCartUpdated.bind(this);

    // Configuration
    this.config = {
      locale: 'fr',
      minDays: 3,
      maxDays: 5,
      noSaturday: false,
      noSunday: false,
      dateFormat: 'in_letters',
      showWeekday: true,
      cutoffHour: null,
    };
  }

  connectedCallback() {
    // Récupérer les éléments DOM
    this.deliveryTextElement = this.querySelector('[data-ref="delivery-text"]');
    this.minDateElement = this.querySelector('[data-ref="min-date"]');
    this.betweenTextElement = this.querySelector('[data-ref="between-text"]');
    this.maxDateElement = this.querySelector('[data-ref="max-date"]');

    // Récupérer la configuration depuis les data attributes
    this.#loadConfiguration();

    // #Purphyt - Debut: Basculer entre estimation et precommande
    // Utilise l'attribut dédié produit (jamais pollué par le fallback panier)
    this.#productPreorderDate = this.dataset.productPreorderDate || '';
    this.#render();
    document.addEventListener(ThemeEvents.variantUpdated, this.handleVariantUpdated);
    document.addEventListener(ThemeEvents.cartUpdated, this.handleCartUpdated);
    // Synchronisation immédiate avec le panier : couvre le cas où ce composant
    // vient d'être re-créé (innerHTML swap) et a manqué l'event cart:updated
    this.handleCartUpdated(null);
    // #Purphyt - Fin: Basculer entre estimation et precommande
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.variantUpdated, this.handleVariantUpdated);
    document.removeEventListener(ThemeEvents.cartUpdated, this.handleCartUpdated);
  }

  handleVariantUpdated(event) {
    if (event.detail?.data?.productId !== this.dataset.productId) return;

    const updatedComponent = event.detail.data.html?.querySelector('delivery-estimation');
    if (!(updatedComponent instanceof HTMLElement)) return;

    // #Purphyt - Met à jour la date produit/variante puis recalcule avec le panier
    this.#productPreorderDate = updatedComponent.dataset.productPreorderDate || '';
    this.dataset.preorderDate = updatedComponent.dataset.preorderDate || '';
    this.dataset.preorderText = updatedComponent.dataset.preorderText || '';
    this.handleCartUpdated();
  }

  // #Purphyt - Debut: Synchronisation avec le panier pour la date de précommande
  async handleCartUpdated() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      let latestCartDate = '';
      for (const item of cart.items) {
        const itemDate = item.properties?._preorder_date;
        // #Purphyt - Filtre les valeurs invalides (ex: "blank" = artefact d'un bug linter)
        if (itemDate && /^\d{4}-\d{2}-\d{2}/.test(itemDate) && (!latestCartDate || itemDate > latestCartDate)) {
          latestCartDate = itemDate;
        }
      }

      // #Purphyt - Uniquement les articles du panier, jamais le produit en cours de visualisation
      const effectiveDate = latestCartDate;

      if (effectiveDate) {
        // #Purphyt - Format long français cohérent avec l'affichage delivery-estimation
        const [year, month, day] = effectiveDate.split('-');
        const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        const formattedDate = new Intl.DateTimeFormat('fr', { day: 'numeric', month: 'long', year: 'numeric' }).format(date);
        this.dataset.preorderDate = effectiveDate;
        this.dataset.preorderText = `Pré-commande - Livraison à partir du ${formattedDate}`;
      } else {
        this.dataset.preorderDate = '';
        this.dataset.preorderText = '';
      }
      this.#render();
    } catch (_) {}
  }
  // #Purphyt - Fin: Synchronisation avec le panier pour la date de précommande

  /**
   * Charge la configuration depuis les data attributes
   */
  #loadConfiguration() {
    this.config.locale = this.dataset.locale || 'fr';
    this.config.minDays = parseInt(this.dataset.minDays) || 3;
    this.config.maxDays = parseInt(this.dataset.maxDays) || 5;
    this.config.noSaturday = this.dataset.noSaturday === 'true';
    this.config.noSunday = this.dataset.noSunday === 'true';
    this.config.dateFormat = this.dataset.dateFormat || 'in_letters';
    this.config.showWeekday = this.dataset.showWeekday === 'true';

    // Gérer l'heure de coupure
    const cutoffHour = this.dataset.cutoffHour;
    if (cutoffHour && cutoffHour !== '') {
      this.config.cutoffHour = parseInt(cutoffHour);
    }
  }

  /**
   * Calcule et affiche les dates d'estimation
   */
  #calculateAndDisplayDates() {
    const minDate = this.#calculateDeliveryDate(this.config.minDays);
    const maxDate = this.#calculateDeliveryDate(this.config.maxDays);

    // Formater et afficher les dates
    if (this.minDateElement) {
      this.minDateElement.textContent = this.#formatDate(minDate);
    }

    if (this.maxDateElement) {
      this.maxDateElement.textContent = this.#formatDate(maxDate);
    }
  }

  // #Purphyt - Debut: Rendu conditionnel precommande vs estimation
  #render() {
    if (this.dataset.preorderDate && this.dataset.preorderText) {
      if (this.deliveryTextElement) {
        this.deliveryTextElement.textContent = this.dataset.preorderText;
      }

      this.minDateElement?.setAttribute('hidden', 'hidden');
      this.betweenTextElement?.setAttribute('hidden', 'hidden');
      this.maxDateElement?.setAttribute('hidden', 'hidden');
      return;
    }

    if (this.deliveryTextElement) {
      this.deliveryTextElement.textContent = this.dataset.deliveryTextDefault || '';
    }

    this.minDateElement?.removeAttribute('hidden');
    this.betweenTextElement?.removeAttribute('hidden');
    this.maxDateElement?.removeAttribute('hidden');
    this.#calculateAndDisplayDates();
  }
  // #Purphyt - Fin: Rendu conditionnel precommande vs estimation

  /**
   * Calcule une date de livraison en tenant compte des jours ouvrables
   * @param {number} daysToAdd - Nombre de jours à ajouter
   * @returns {Date} - Date de livraison calculée
   */
  #calculateDeliveryDate(daysToAdd) {
    const now = new Date();
    let startDate = new Date();

    // Vérifier l'heure de coupure
    if (this.config.cutoffHour !== null) {
      const currentHour = now.getHours();
      if (currentHour >= this.config.cutoffHour) {
        // Après l'heure de coupure, on commence le calcul à partir de demain
        startDate.setDate(startDate.getDate() + 1);
      }
    }

    // Calculer la date de livraison en excluant les jours non ouvrables
    let businessDaysAdded = 0;
    let currentDate = new Date(startDate);

    while (businessDaysAdded < daysToAdd) {
      currentDate.setDate(currentDate.getDate() + 1);

      const dayOfWeek = currentDate.getDay();
      const isSaturday = dayOfWeek === 6;
      const isSunday = dayOfWeek === 0;

      // Vérifier si c'est un jour ouvrable
      const isBusinessDay = !((isSaturday && this.config.noSaturday) || (isSunday && this.config.noSunday));

      if (isBusinessDay) {
        businessDaysAdded++;
      }
    }

    // Si la date finale tombe sur un jour non ouvrable, avancer au prochain jour ouvrable
    while (this.#isNonDeliveryDay(currentDate)) {
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return currentDate;
  }

  /**
   * Vérifie si une date est un jour non ouvrable
   * @param {Date} date - Date à vérifier
   * @returns {boolean} - True si c'est un jour non ouvrable
   */
  #isNonDeliveryDay(date) {
    const dayOfWeek = date.getDay();
    const isSaturday = dayOfWeek === 6;
    const isSunday = dayOfWeek === 0;

    return (isSaturday && this.config.noSaturday) || (isSunday && this.config.noSunday);
  }

  /**
   * Formate une date selon la configuration
   * @param {Date} date - Date à formater
   * @returns {string} - Date formatée
   */
  #formatDate(date) {
    // Déterminer les options de formatage selon la configuration
    const options = {};

    if (this.config.dateFormat === 'numbers') {
      // Format numérique (ex: 24/12)
      options.day = 'numeric';
      options.month = 'numeric';
    } else {
      // Format en lettres (ex: 24 décembre)
      options.day = 'numeric';
      options.month = 'long';
    }

    // Ajouter le jour de la semaine si demandé
    if (this.config.showWeekday) {
      options.weekday = 'long';
    }

    // Utiliser Intl.DateTimeFormat pour formater selon la locale
    const formatter = new Intl.DateTimeFormat(this.config.locale, options);

    // Formater la date
    let formattedDate = formatter.format(date);

    // Capitaliser la première lettre si nécessaire
    formattedDate = this.#capitalizeFirstLetter(formattedDate);

    return formattedDate;
  }

  /**
   * Capitalise la première lettre d'une chaîne
   * @param {string} str - Chaîne à capitaliser
   * @returns {string} - Chaîne avec première lettre en majuscule
   */
  #capitalizeFirstLetter(str) {
    if (!str) return str;
    return str.charAt(0).toUpperCase() + str.slice(1);
  }
}

if (!customElements.get('delivery-estimation')) {
  customElements.define('delivery-estimation', DeliveryEstimation);
}
